// Unsaved-settings detection.
//
// The Settings page edits a local draft and only persists when "Save
// settings" is pressed. Without a dirty check the page silently discards
// edits on navigation — which is exactly how a newly added fee earner
// ("Sarah", 2026-08-05) vanished: the row was typed, never saved, and
// the draft reset on the next mount.

/** Deep structural comparison, insensitive to object key order. */
export function settingsEqual(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

function canonical(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null) ?? 'null';
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(',')}}`;
}

export interface FeeEarnerLike {
  code: string;
  aliases: string[];
  displayName?: string;
}

/**
 * Drop fee-earner rows the user added but never filled in, so an
 * accidental "+ Add fee earner" click cannot persist a blank entry.
 * Rows with any content are preserved untouched for validation.
 */
export function dropEmptyFeeEarners<T extends FeeEarnerLike>(list: T[]): T[] {
  return list.filter(
    (fe) =>
      fe.code.trim().length > 0 ||
      fe.aliases.some((a) => a.trim().length > 0) ||
      (fe.displayName ?? '').trim().length > 0,
  );
}

/** Fee earners that carry a name/alias but no code — unusable for export. */
export function feeEarnersMissingCode<T extends FeeEarnerLike>(list: T[]): T[] {
  return dropEmptyFeeEarners(list).filter((fe) => fe.code.trim().length === 0);
}
